import React, { useState, useEffect, useRef } from "react";

const AnimatedElement = ({
  children,
  className = "",
  threshold = 0.15,
  delay = 0,
  duration = 800,
  type = "fadeUp",

  replay = true,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (!replay) observer.unobserve(entry.target);
        } else {
          if (replay) setVisible(false);
        }
      },
      { threshold }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, [threshold, replay]);

  const base = {
    transition: `all ${duration}ms cubic-bezier(0.25, 1, 0.5, 1) ${delay}ms`,
    willChange: "transform, opacity, filter",
  };

  const variants = {
    // 🔹 FADE + SLIDE
    fadeUp: {
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0px)" : "translateY(40px)",
    },

    fadeDown: {
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0px)" : "translateY(-40px)",
    },

    fadeLeft: {
      opacity: visible ? 1 : 0,
      transform: visible ? "translateX(0px)" : "translateX(-60px)",
    },

    fadeRight: {
      opacity: visible ? 1 : 0,
      transform: visible ? "translateX(0px)" : "translateX(60px)",
    },

    // 🔹 SCALE / ZOOM
    zoomIn: {
      opacity: visible ? 1 : 0,
      transform: visible ? "scale(1)" : "scale(0.7)",
    },

    zoomOut: {
      opacity: visible ? 1 : 0,
      transform: visible ? "scale(1)" : "scale(1.2)",
    },

    // 🔹 BLUR EFFECTS
    blurIn: {
      opacity: visible ? 1 : 0,
      transform: visible ? "scale(1)" : "scale(1.05)",
      filter: visible ? "blur(0px)" : "blur(10px)",
    },

    blurUp: {
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0px)" : "translateY(30px)",
      filter: visible ? "blur(0px)" : "blur(8px)",
    },

    // 🔹 ROTATE
    rotateIn: {
      opacity: visible ? 1 : 0,
      transform: visible ? "rotate(0deg) scale(1)" : "rotate(-10deg) scale(0.9)",
    },

    rotateRight: {
      opacity: visible ? 1 : 0,
      transform: visible ? "rotate(0deg)" : "rotate(8deg)",
    },

    // 🔹 SKEW (خیلی مدرن)
    skewIn: {
      opacity: visible ? 1 : 0,
      transform: visible ? "skew(0deg)" : "skew(-10deg)",
    },

    // 🔹 3D STYLE (خیلی سینمایی)
    flipX: {
      opacity: visible ? 1 : 0,
      transform: visible
        ? "perspective(800px) rotateX(0deg)"
        : "perspective(800px) rotateX(70deg)",
    },

    flipY: {
      opacity: visible ? 1 : 0,
      transform: visible
        ? "perspective(800px) rotateY(0deg)"
        : "perspective(800px) rotateY(70deg)",
    },

    pop: {
      opacity: visible ? 1 : 0,
      transform: visible ? "scale(1)" : "scale(0.5)",
    },
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...base,
        ...variants[type],
      }}
    >
      {children}
    </div>
  );
};

export default AnimatedElement;